import React, { useEffect, useState } from 'react';
import { formatDttm } from '../../Util/timezone';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Typography,
  makeStyles,
} from '@material-ui/core';

import DuplicateMergeReview from '../Admin/DuplicateDetection/DuplicateMergeReview';

const PRODUCT_FIELDS = [
  { key: 'style',               label: 'Style Number'    },
  { key: 'trade_name',          label: 'Style Name'      },
  { key: 'material_class',      label: 'Material Class'  },
  { key: 'material_sub_class',  label: 'Sub Class'       },
  { key: 'size_cd',             label: 'Size'            },
  { key: 'backing_cd',          label: 'Backing'         },
  { key: 'collection',          label: 'Collection'      },
  { key: 'private_style_number',label: 'Private Style #' },
  { key: 'private_style_name',  label: 'Private Name'   },
  { key: 'display_name',        label: 'Display Name'    },
];

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingRight: theme.spacing(2),
  },
  xButton: { minWidth: 36, padding: '4px 8px' },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  sectionCount: {
    display: 'inline-block',
    backgroundColor: '#ececec',
    borderRadius: 10,
    padding: '0 8px',
    fontSize: 12,
    fontWeight: 700,
    color: '#555',
  },
  mergedCard: {
    padding: theme.spacing(2),
    height: '100%',
    boxSizing: 'border-box',
    border: '1px solid #e0e0e0',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    borderBottom: '1px solid #e0e0e0',
  },
  mfgName: { fontWeight: 700, fontSize: 15 },
  lastModified: { fontSize: 11, color: '#888', marginTop: 2 },
  mergedBadge: {
    display: 'inline-block',
    backgroundColor: '#fff3e0',
    color: '#e65100',
    border: '1px solid #ffcc80',
    fontSize: 11,
    fontWeight: 700,
    padding: '3px 10px',
    borderRadius: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    flexShrink: 0,
    marginLeft: theme.spacing(1),
  },
  fieldRow: {
    display: 'flex',
    padding: '4px 6px',
    borderRadius: 3,
    marginBottom: 2,
  },
  fieldRowDiff: {
    backgroundColor: '#fff8e1',
    borderLeft: '3px solid #f57c00',
    paddingLeft: 5,
  },
  fieldLabel: {
    color: '#888',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    minWidth: 120,
    flexShrink: 0,
    paddingTop: 1,
  },
  fieldValue: { fontSize: 13, color: '#222' },
  fieldEmpty: { fontSize: 13, color: '#bbb', fontStyle: 'italic' },
  diffLegend: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
    color: '#666',
    marginBottom: theme.spacing(1),
    padding: '5px 8px',
    backgroundColor: '#fffde7',
    borderRadius: 4,
    border: '1px solid #ffe082',
  },
  diffDot: {
    width: 10,
    height: 10,
    backgroundColor: '#f57c00',
    borderRadius: 2,
    flexShrink: 0,
  },
  unmergeButton: { marginTop: theme.spacing(1.5) },
  mergeButton: {
    backgroundColor: '#FF682C',
    color: '#fff',
    '&:hover': { backgroundColor: '#e55520' },
    '&:disabled': { backgroundColor: '#ccc', color: '#fff' },
  },
  emptyHint: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: '#888',
  },
  singleProductWrap: {
    maxWidth: 600,
    margin: '0 auto',
  },
}));

function MergedProductCard({ product, parent, onUnmerge, isUnmerging }) {
  const classes = useStyles();

  const diffFields = new Set(
    PRODUCT_FIELDS
      .filter(({ key }) => parent && (product[key] || '') !== (parent[key] || ''))
      .map(({ key }) => key)
  );

  const visibleFields = PRODUCT_FIELDS.filter(
    ({ key }) => diffFields.has(key) || (product[key] != null && product[key] !== '')
  );

  return (
    <Paper elevation={1} className={classes.mergedCard}>
      <Box className={classes.cardHeader}>
        <Box style={{ minWidth: 0 }}>
          <Typography className={classes.mfgName}>{product.manufacturer_name}</Typography>
          <Typography className={classes.lastModified}>
            Last modified:{' '}
            {product.last_modified_dttm
              ? formatDttm(product.last_modified_dttm)
              : 'Unknown'}
          </Typography>
        </Box>
        <span className={classes.mergedBadge}>Was Merged</span>
      </Box>

      {diffFields.size > 0 && (
        <Box className={classes.diffLegend}>
          <span className={classes.diffDot} />
          Fields that differ from the surviving product
        </Box>
      )}

      {visibleFields.map(({ key, label }) => {
        const isDiff = diffFields.has(key);
        const value = product[key];
        return (
          <Box
            key={key}
            className={`${classes.fieldRow} ${isDiff ? classes.fieldRowDiff : ''}`}
          >
            <Typography className={classes.fieldLabel}>{label}</Typography>
            {value != null && value !== '' ? (
              <Typography className={classes.fieldValue}>{value}</Typography>
            ) : (
              <Typography className={classes.fieldEmpty}>—</Typography>
            )}
          </Box>
        );
      })}

      <Button
        className={classes.unmergeButton}
        size="small"
        variant="outlined"
        onClick={onUnmerge}
        disabled={isUnmerging}
        fullWidth
      >
        {isUnmerging ? (
          <><CircularProgress size={14} style={{ marginRight: 6 }} />Unmerging…</>
        ) : (
          'Unmerge'
        )}
      </Button>
    </Paper>
  );
}

function SingleProductView({ product }) {
  const classes = useStyles();
  const visibleFields = PRODUCT_FIELDS.filter(
    ({ key }) => product[key] != null && product[key] !== ''
  );

  return (
    <Box className={classes.singleProductWrap}>
      <Paper elevation={1} className={classes.mergedCard}>
        <Box className={classes.cardHeader}>
          <Box>
            <Typography className={classes.mfgName}>{product.manufacturer_name}</Typography>
            <Typography className={classes.lastModified}>
              Last modified:{' '}
              {product.last_modified_dttm
                ? formatDttm(product.last_modified_dttm)
                : 'Unknown'}
            </Typography>
          </Box>
        </Box>
        {visibleFields.map(({ key, label }) => (
          <Box key={key} className={classes.fieldRow}>
            <Typography className={classes.fieldLabel}>{label}</Typography>
            <Typography className={classes.fieldValue}>{product[key]}</Typography>
          </Box>
        ))}
      </Paper>
    </Box>
  );
}

const MergeDialog = ({ open, productList, onMerge, onUnmerge, onClose }) => {
  const classes = useStyles();
  const [keeperUuid, setKeeperUuid] = useState('');
  const [merging, setMerging] = useState(false);
  const [unmerging, setUnmerging] = useState(new Set());

  useEffect(() => {
    if (open) setKeeperUuid('');
  }, [open, productList]);

  const list = productList || [];
  const primaryProducts = list.filter((p) => !p.merged_product_uuid);
  const mergedProducts  = list.filter((p) =>  !!p.merged_product_uuid);

  const tooMany = primaryProducts.length > 2;
  const canMerge = primaryProducts.length === 2;

  const title = canMerge
    ? 'Compare & Merge Products'
    : mergedProducts.length > 0
    ? 'Merged Product Group'
    : 'Product Details';

  const handleMerge = () => {
    if (!canMerge || !keeperUuid) return;
    const loserUuid = keeperUuid === primaryProducts[0].product_uuid
      ? primaryProducts[1].product_uuid
      : primaryProducts[0].product_uuid;
    setMerging(true);
    onMerge(keeperUuid, [loserUuid]).finally(() => setMerging(false));
  };

  const handleUnmerge = (uuid) => {
    setUnmerging((prev) => new Set([...prev, uuid]));
    onUnmerge([uuid]).finally(() =>
      setUnmerging((prev) => {
        const next = new Set(prev);
        next.delete(uuid);
        return next;
      })
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6">{title}</Typography>
        <Button className={classes.xButton} variant="outlined" onClick={onClose}>
          X
        </Button>
      </DialogTitle>

      <DialogContent dividers>
        {tooMany ? (
          <Box className={classes.emptyHint}>
            <Typography>
              Please select 1 or 2 products to compare. Deselect some and try again.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Side-by-side comparison */}
            {canMerge && (
              <DuplicateMergeReview
                productA={primaryProducts[0]}
                productB={primaryProducts[1]}
                keeperUuid={keeperUuid}
                onKeep={setKeeperUuid}
                mode="merge"
              />
            )}

            {/* Single unmerged product */}
            {primaryProducts.length === 1 && (
              <SingleProductView product={primaryProducts[0]} />
            )}

            {/* Previously merged into this group */}
            {mergedProducts.length > 0 && (
              <Box mt={3}>
                <Divider />
                <Box mt={2.5} className={classes.sectionHeader}>
                  <Typography variant="h6">Previously merged into this product</Typography>
                  <span className={classes.sectionCount}>{mergedProducts.length}</span>
                </Box>
                <Grid container spacing={2}>
                  {mergedProducts.map((mp) => {
                    const parent = list.find(
                      (p) => p.product_uuid === mp.merged_product_uuid
                    );
                    return (
                      <Grid item xs={12} md={6} lg={4} key={mp.product_uuid}>
                        <MergedProductCard
                          product={mp}
                          parent={parent}
                          onUnmerge={() => handleUnmerge(mp.product_uuid)}
                          isUnmerging={unmerging.has(mp.product_uuid)}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}

            {primaryProducts.length === 1 && mergedProducts.length === 0 && (
              <Box className={classes.emptyHint}>
                <Typography>No products have been merged into this one.</Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={merging}>
          Cancel
        </Button>
        {canMerge && (
          <Button
            variant="contained"
            className={classes.mergeButton}
            onClick={handleMerge}
            disabled={!keeperUuid || merging}
          >
            {merging ? (
              <><CircularProgress size={16} style={{ color: '#fff', marginRight: 8 }} />Merging…</>
            ) : keeperUuid ? (
              'Confirm Merge'
            ) : (
              'Select a product to keep'
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MergeDialog;
